import { inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LocalizationDTO, MessageDTO, MessageTypeDTO } from '@api';
import { I18Service, Language } from '@translation';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private i18Service = inject(I18Service);
  activeLanguage: Language;

  readonly errorAlertContent = signal<string | undefined>(undefined);
  readonly errorAlertButton = signal<string | undefined>(undefined);

  constructor() {
    this.activeLanguage = this.i18Service.getActiveLanguage();
  }

  clearError() {
    this.errorAlertContent.set(undefined);
    this.errorAlertButton.set(undefined);
  }

  handleError(error: HttpErrorResponse): void {
    this.errorAlertContent.set(this.defineErrorAlertContent(error));
    this.errorAlertButton.set(this.defineErrorAlertButton(error));
  }

  defineErrorAlertContent(errorResponse: HttpErrorResponse): string {
    let error = errorResponse.error;
    if (typeof error === 'string') {
      try {
        error = JSON.parse(error);
      } catch (e) {
        console.log(e);
      }
    }

    if (!error) {
      return `${errorResponse.statusText} (${errorResponse.status})`;
    }

    let content: string;
    if (error.messages && error.localizations) {
      const localizations: LocalizationDTO =
        error.localizations.find((localizations: LocalizationDTO) =>
          this.activeLanguage.includes(localizations.locale),
        ) || error.localizations.find((localizations: LocalizationDTO) => localizations.locale == 'en');

      if (!localizations) {
        return `localization of error message failed for language ${JSON.stringify(this.activeLanguage)}`;
      }

      const errorIds = error.messages
        .filter((message: MessageDTO) => message.type === MessageTypeDTO.ERROR)
        .map((message: MessageDTO) => message.id);

      content = errorIds
        .map((id: string) => {
          const message = localizations.items[id];
          if (message) {
            return `${id}\n${message}`;
          }
          return undefined;
        })
        .filter((message: string | undefined) => !!message)
        .join('\n\n');
    } else {
      if (error.message) {
        content = error.message;
      } else if (error.error) {
        content = error.error;
      } else if (typeof error !== 'object') {
        content = error;
      } else {
        content = 'Error';
      }
    }

    return content;
  }

  defineErrorAlertButton(errorResponse: HttpErrorResponse): string {
    let buttonKey: string;
    switch (errorResponse.status) {
      case 403:
      case 500:
      case 502:
      case 503:
      case 504:
      case 401:
      default:
        buttonKey = 'ok';
        break;
    }
    return buttonKey;
  }
}
